import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import {
  fetchGateway, saveGateway, testConnection,
  fetchTemplates, createTemplate, updateTemplate,
} from './api';
import type { WhatsAppSettingsInput, WhatsAppTemplateInput } from './types';

/** `enabled: false` for a viewer without the admin:whatsapp-gateway grant (the shared Notifications tab). */
export function useWhatsAppGateway({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: keys.admin.whatsappGateway(), queryFn: fetchGateway, enabled });
}

/** Saving re-reads the templates too: a switch changes what their readiness badges say. */
export function useSaveWhatsAppGateway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WhatsAppSettingsInput) => saveGateway(data),
    onSuccess: (data) => {
      qc.setQueryData(keys.admin.whatsappGateway(), data);
      qc.invalidateQueries({ queryKey: keys.admin.whatsappTemplates() });
    },
  });
}

export function useWhatsAppTestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WhatsAppSettingsInput) => testConnection(data),
    onSettled: () => { qc.invalidateQueries({ queryKey: keys.admin.whatsappGateway() }); },
  });
}

/** `enabled: false` for a viewer without the admin:whatsapp-gateway grant (the shared Delivery log). */
export function useWhatsAppTemplates({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: keys.admin.whatsappTemplates(), queryFn: fetchTemplates, enabled });
}

export function useSaveWhatsAppTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: WhatsAppTemplateInput }) =>
      (id ? updateTemplate(id, data) : createTemplate(data)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.admin.whatsappTemplates() }); },
  });
}

